import React, { useState } from 'react';
import { Box, Button, TextField, Typography, InputAdornment, IconButton, Dialog, DialogContent } from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { api } from '../../utils/api';
import ForgotPasswordInitial from './ForgotPassword/ForgotPasswordInitial';
import VerifyCode from './ForgotPassword/VerifyCode';
import ResetPassword from './ForgotPassword/ResetPassword';

export default function EmailLogin({ 
    formData, 
    onInputChange, 
    onSubmit, 
    onBack, 
    onSignup,
    showPassword,
    onTogglePassword 
}) {
    const [forgotPasswordStep, setForgotPasswordStep] = useState(null);
    const [resetEmail, setResetEmail] = useState('');
    const [resetToken, setResetToken] = useState('');
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    const handleForgotPassword = async (email) => {
        try {
            const response = await api.post('/api/auth/forgot-password', { email });
            if (response.success) {
                setResetEmail(email);
                setForgotPasswordStep('verify');
            } else {
                throw new Error(response.message || 'Failed to send verification code');
            }
        } catch (error) {
            throw new Error(error.message || 'Failed to send verification code');
        }
    };

    const handleVerifyCode = async (code) => {
        try {
            const response = await api.post('/api/auth/verify-reset-code', { 
                email: resetEmail, 
                code 
            });
            if (response.success && response.resetToken) {
                setResetToken(response.resetToken);
                setForgotPasswordStep('reset');
            } else {
                throw new Error(response.message || 'Invalid verification code');
            }
        } catch (error) {
            throw new Error(error.message || 'Invalid verification code');
        }
    };

    const handleResetPassword = async (newPassword) => {
        try {
            const response = await api.post('/api/auth/reset-password', { 
                email: resetEmail,
                resetToken,
                newPassword
            });
            if (response.success) {
                setForgotPasswordStep(null);
                setShowSuccessDialog(true);
            } else {
                throw new Error(response.message || 'Failed to reset password');
            }
        } catch (error) {
            throw new Error(error.message || 'Failed to reset password');
        }
    };

    return (
        <>
            <form onSubmit={onSubmit} style={{ width: '100%' }}>
                <Box sx={{ 
                    position: 'relative', 
                    mb: 3,
                    mt: -1
                }}>
                    <IconButton
                        onClick={onBack}
                        sx={{ 
                            color: '#2B0063',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            p: 1,
                            '&:hover': {
                                background: 'none',
                                opacity: 0.8
                            }
                        }}
                    >
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" align="center" gutterBottom>
                        Sign In
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        name="email"
                        type="email"
                        label="Email"
                        value={formData.email}
                        onChange={onInputChange}
                        fullWidth
                        required
                    />
                    <TextField
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        label="Password"
                        value={formData.password}
                        onChange={onInputChange}
                        fullWidth
                        required
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={onTogglePassword}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'flex-end',
                        mt: -1, 
                        mb: 1 
                    }}>
                        <Button
                            variant="text"
                            onClick={() => setForgotPasswordStep('initial')}
                            sx={{ 
                                textTransform: 'none',
                                color: '#2B0063',
                                fontWeight: 'bold',
                                p: 0,
                                minWidth: 'auto',
                                '&:hover': {
                                    background: 'none',
                                    textDecoration: 'underline'
                                }
                            }}
                        >
                            Forgot password?
                        </Button>
                    </Box>
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        sx={{ 
                            mt: 1,
                            bgcolor: '#2B0063',
                            '&:hover': {
                                bgcolor: '#1a003d'
                            }
                        }}
                    >
                        Sign In
                    </Button>
                    <Typography color="textSecondary" align="center" sx={{ mt: 2 }}>
                        Don't have an account?{' '}
                        <Button
                            variant="text"
                            onClick={onSignup}
                            sx={{ 
                                textTransform: 'none',
                                color: '#2B0063',
                                fontWeight: 'bold',
                                p: 0,
                                minWidth: 'auto',
                                verticalAlign: 'baseline'
                            }}
                        >
                            Sign up here
                        </Button>
                    </Typography>
                </Box>
            </form>

            <ForgotPasswordInitial
                open={forgotPasswordStep === 'initial'}
                onClose={() => setForgotPasswordStep(null)}
                onSubmit={handleForgotPassword}
            />

            <VerifyCode
                open={forgotPasswordStep === 'verify'}
                onClose={() => setForgotPasswordStep(null)}
                onSubmit={handleVerifyCode}
                onResendCode={() => handleForgotPassword(resetEmail)}
                email={resetEmail}
            />

            <ResetPassword
                open={forgotPasswordStep === 'reset'}
                onClose={() => setForgotPasswordStep(null)}
                onSubmit={handleResetPassword}
            />

            <Dialog 
                open={showSuccessDialog} 
                maxWidth="sm" 
                fullWidth
                PaperProps={{
                    sx: {
                        width: '100%',
                        maxWidth: '444px',
                        margin: '20px'
                    }
                }}
            >
                <DialogContent className="forgot-password-dialog">
                    <Typography className="forgot-password-title">
                        Password Reset Successful
                    </Typography>
                    <Typography className="forgot-password-subtitle">
                        Your password has been successfully reset. Please sign in with your new password.
                    </Typography>
                    <Button
                        className="forgot-password-button"
                        onClick={() => {
                            setShowSuccessDialog(false);
                            setResetEmail('');
                        }}
                    >
                        Sign In
                    </Button>
                </DialogContent>
            </Dialog>
        </>
    );
} 